import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
} from '@angular/core';
import { BudgetDetails } from 'src/interfaces/budget-details';
import { SharedModule } from '../../_shared/shared.module';
import { FullScreenDialogConfig } from 'src/app/_config/full-screen-dialog-config';
import { MatDialog } from '@angular/material/dialog';
import { BudgetDetailsDialogComponent } from '../budget-details-dialog/budget-details-dialog.component';
import { BudgetDetailsService } from 'src/app/_core/services/budget-details.service';
import { BehaviorSubject, of, switchMap, tap } from 'rxjs';
import { ConfirmDialogConfig } from 'src/app/_config/confirm-dialog-config';
import { MessageDialogComponent } from 'src/app/_shared/message-dialog/message-dialog.component';
import { DialogService } from 'src/app/_core/services/dialog.service';
import { PraticaProgressService } from 'src/app/_core/services/pratica-progress.service';
import { IconService } from 'src/app/_core/services/icon.service';

import { BudgetDetailsEditYearsDialogComponent } from '../budget-details-edit-years-dialog/budget-details-edit-years-dialog.component';
import { LabelPipeModule } from '../../_shared/label-pipe/label-pipe.module';

@Component({
  selector: 'app-budget-details-table',
  standalone: true,
  imports: [SharedModule, LabelPipeModule],
  templateUrl: './budget-details-table.component.html',
  styleUrls: ['./budget-details-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetDetailsTableComponent implements OnDestroy {
  @Input()
  set idScheda(presentationSheetId: string) {
    if (presentationSheetId) {
      this._idScheda = presentationSheetId;
      this.getTableData().subscribe();
    }
  }

  private _idScheda: string = '';

  get idScheda() {
    return this._idScheda;
  }

  dataSource: BudgetDetails[] = [];

  completionPercentage = new BehaviorSubject<number>(0);

  blockingKpiHasError = false;
  couldntRetrieveData = false;

  private totalNumberOfBudgetDetails = 0;

  private readonly percentageWeight = 1 / 3;

  budgetDetails: BudgetDetails[] = [];

  constructor(
    private dialog: MatDialog,
    private budgetDetailsService: BudgetDetailsService,
    private dialogService: DialogService,
    public praticaProgressService: PraticaProgressService,
    iconService: IconService,
    private cdr: ChangeDetectorRef
  ) {
    iconService.getIcons(['edit', 'delete', 'eye']);
  }

  ngAfterViewInit(): void {
    //this.getTableData().subscribe();
  }

  ngOnDestroy(): void {
    this.completionPercentage.complete();
  }

  getTableData() {
    return this.budgetDetailsService.getById(this.idScheda).pipe(
      tap((res) => {
        this.budgetDetails = res;
        if (!res[0]?.categoria) {
          this.dataSource = res;
        } else {
          let tableData: BudgetDetails[] = [];
          res.forEach((el, i) => {
            if (i === 0 || el.categoria !== res[i - 1].categoria) {
              tableData.push({
                categoria: el.categoria,
                current: 0,
                previus: 0,
                note: '',
                order: 0,
                id: '',
                idScheda: '',
                label: '',
              });
            }
            if (el.visibile) {
              tableData.push(el);
            }
          });
          this.dataSource = tableData;
        }
        let roundedCompletionPercentage = 100;

        if (this.praticaProgressService.selectedPratica?.type === 'FIN') {
          this.totalNumberOfBudgetDetails = this.dataSource.length;
          this.blockingKpiHasError = this.calcIfKpiSumIsGreaterThanZero(res, [
            'creditiVersoClientiEntroEsercizio',
            'creditiVersoClientiOltreEsercizio',
            'debitiTributariEntroEsercizio',
            'debitiTributariOltreEsercizio',
            'debitiVersoIstitutiEntroEsercizio',
            'debitiVersoIstitutiOltreEsercizio',
            'debitiVersoFornitoriEntroEsercizio',
            'debitiVersoFornitoriOltreEsercizio',
            'debitiVersoBancheEntroEsercizio',
            'debitiVersoBancheOltreEsercizio',
          ]);

          this.couldntRetrieveData = this.calcIfKpiSumIsGreaterThanZero(res);

          let totalRequiredInfo = 2;
          let completedInfo = 0;
          completedInfo = completedInfo + (!this.blockingKpiHasError ? 1 : 0);
          completedInfo = completedInfo + (!this.couldntRetrieveData ? 1 : 0);
          let completionPercentageWithDecimals =
            (completedInfo / totalRequiredInfo) * 100;
          roundedCompletionPercentage = Math.round(
            completionPercentageWithDecimals
          );
        }
        let completionPercentage =
          this.praticaProgressService.calcComplexCompletionPercentage(
            roundedCompletionPercentage,
            this.percentageWeight
          );
        this.completionPercentage.next(completionPercentage);
        this.cdr.detectChanges();
      })
    );
  }

  openBudgetDialog(type: 'budgetData' | 'budgetYears') {
    if (
      this.praticaProgressService.isFrozen &&
      !this.praticaProgressService.canEditDataOnSignedPratica
    )
      return;
    (type === 'budgetData'
      ? this.showBudgetDetailsDialog()
      : this.showYearsDialog()
    )
      .pipe(
        switchMap((hasSaved) => (hasSaved ? this.getTableData() : of(null)))
      )
      .subscribe();
  }

  showBudgetDetailsDialog() {
    return this.dialogService
      .open(BudgetDetailsDialogComponent, {
        ...FullScreenDialogConfig,
        data: {
          uploadedBudgetData: this.budgetDetails,
          idScheda: this.idScheda,
        },
      })
      .afterClosed();
  }

  showYearsDialog() {
    return this.dialogService
      .open(BudgetDetailsEditYearsDialogComponent, {
        data: {
          previous: this.dataSource[0]?.annoPrecedente,
          current: this.dataSource[0]?.annoCorrente,
          id: this.idScheda,
        },
      })
      .afterClosed();
  }

  onDeleteBudgetDetails(event: BudgetDetails) {
    this.openConfirmDialog()
      .pipe(
        switchMap((confirm) => (confirm ? this.onDelete(event.id) : of(null)))
      )
      .subscribe();
  }

  private onDelete(id: string) {
    return this.budgetDetailsService.delete(id).pipe(
      tap((res) => {
        this.dialogService.onSuccess();
      }),
      switchMap((_) => this.getTableData())
    );
  }

  private openConfirmDialog() {
    return this.dialog
      .open(MessageDialogComponent, new ConfirmDialogConfig())
      .afterClosed();
  }

  displayedColumns: (keyof BudgetDetails)[] = [
    'name',
    'current',
    'previus',
    'note',
  ];

  get isMaxAmountOfDataReached() {
    return this.dataSource.length >= this.totalNumberOfBudgetDetails;
  }

  calcIfKpiSumIsGreaterThanZero(data?: BudgetDetails[], kpiKeys?: string[]) {
    let sum = data
      ?.filter((el) => {
        return kpiKeys ? kpiKeys.includes(el.name!) : true;
      })
      .map((el) => {
        const amount = Number(el.current);
        return !isNaN(amount) ? amount : 0;
      })
      .reduce((a, b) => a + b);
    let isGreaterThanZero = (sum || 0) > 0;
    return !isGreaterThanZero;
  }
}
